import { RecoveryPointAttemptDto } from 'src/app/services/dtos'

// Days of staleness covered by the showcase (1d through 15d). The .5
// offset on the gap-end places the last data point clearly inside the
// "N days ago" gap rather than on the boundary.
const STALE_DAYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const

type StaleDayKey = `stale${(typeof STALE_DAYS)[number]}d`

export type ChartScenarioKey =
  | 'healthy'
  | 'single-point'
  | 'two-points-fresh'
  | 'all-failed'
  | 'long-history'
  | 'real-time'
  | 'burst-then-quiet'
  | 'boundary-7d'
  | 'empty'
  | 'loading'
  | StaleDayKey

export interface ChartScenario {
  key: ChartScenarioKey
  label: string
  description: string
}

export const CHART_SCENARIOS: ReadonlyArray<ChartScenario> = [
  { key: 'healthy', label: 'Healthy', description: 'Recent backups every 5 min, fresh as of now.' },
  {
    key: 'single-point',
    label: 'Single point',
    description: 'Exactly one successful backup ~1 hour ago. Exposes the length−1 iteration in the data builder.',
  },
  {
    key: 'two-points-fresh',
    label: 'Two points',
    description: 'Two consecutive backups in the last 10 minutes — minimum viable rendering.',
  },
  {
    key: 'all-failed',
    label: 'All failed',
    description: 'Five recent attempts, none with createSuccess — successful filter is empty.',
  },
  {
    key: 'long-history',
    label: 'Long history (60d)',
    description: '60 days of dense history, last point ~10 min ago. Tests pan/zoom beyond 2W.',
  },
  {
    key: 'real-time',
    label: 'Real-time (rpo=0)',
    description: 'rpo=0 with replication-latency samples instead of snapshot durations.',
  },
  {
    key: 'burst-then-quiet',
    label: 'Burst then quiet',
    description: 'Dense 12-hour burst 8 days ago, then total silence — clustered shape.',
  },
  {
    key: 'boundary-7d',
    label: 'Boundary — exactly 7d',
    description: 'Last point lands exactly 7 days ago. Tests the 1W threshold edge.',
  },
  ...STALE_DAYS.map<ChartScenario>((d) => ({
    key: `stale${d}d` as StaleDayKey,
    label: `No data — ${d} day${d === 1 ? '' : 's'}`,
    description: `Last successful backup ~${d}.5 days ago.`,
  })),
  { key: 'empty', label: 'No data ever', description: 'Backups loaded but the array is empty.' },
  { key: 'loading', label: 'Loading', description: 'recoveryPointAttempts is still null.' },
]

/**
 * RPO target (in seconds) the chart should show as a horizontal reference
 * line for a given scenario. Most scenarios use 5 min (a typical production
 * value); real-time uses 0 to flip the chart into replication-latency mode.
 */
export function getMockChartRpo(scenario: ChartScenarioKey): number {
  return scenario === 'real-time' ? 0 : 300
}

const FIVE_MIN_MS = 5 * 60 * 1000

const ONE_DAY_MS = 24 * 60 * 60 * 1000

/**
 * Build a series of RecoveryPointAttemptDto from `historyEndDaysAgo` ago up
 * to `lastAttemptDaysAgo` ago, one every ~5 minutes. Snapshot duration jitter
 * is deterministic (sin-based) so each render of the same scenario looks
 * identical.
 */
function buildAttempts(historyEndDaysAgo: number, lastAttemptDaysAgo: number): RecoveryPointAttemptDto[] {
  const now = Date.now()
  const startMs = now - historyEndDaysAgo * ONE_DAY_MS
  const endMs = now - lastAttemptDaysAgo * ONE_DAY_MS
  const attempts: RecoveryPointAttemptDto[] = []
  let i = 0
  for (let t = startMs; t <= endMs; t += FIVE_MIN_MS) {
    // Deterministic jitter — duration drifts between ~30s and ~6min so the
    // line has visible undulation. Occasional spikes simulate slow snapshots.
    const base = 60 + 90 * (Math.sin(i * 0.13) + 1) // 60–240s
    const spike = i % 47 === 0 ? 200 : 0 // ~every 47th sample, push a spike
    const durationSec = Math.round(base + spike)
    const timestamp = new Date(t)
    const createSuccess = new Date(t + durationSec * 1000)
    attempts.push({
      timestamp,
      createSuccess,
      recoveryPointId: `mock-rp-${i}`,
      maxReplicationLatency: null,
    } as RecoveryPointAttemptDto)
    i++
  }
  return attempts
}

/**
 * Resolve a chart scenario key to the input the rpo-chart component expects.
 * `null` is the "loading" sentinel — the chart doesn't render. `[]` is
 * "loaded but empty" — the chart renders with the no-data overlay.
 *
 * For `staleNd` scenarios: history starts max(N + 7, 14) days back so there
 * is always at least 7 days of context behind the gap, and the last point
 * lands at N + 0.5 days ago (clearly inside the "N days no data" window).
 */
export function buildMockChartData(scenario: ChartScenarioKey): RecoveryPointAttemptDto[] | null {
  if (scenario === 'loading') return null
  if (scenario === 'empty') return []
  if (scenario === 'healthy') {
    // 7 days history, last point ~2 min ago.
    return buildAttempts(7, 2 / (60 * 24))
  }
  if (scenario === 'single-point') {
    // Exactly one successful attempt 1 hour ago. computeBriefRpoData /
    // computeNotBriefRpoData both iterate length−1 → produce zero data
    // points from a single attempt. Renders as empty even though there
    // IS a successful backup. Worth surfacing.
    const t = Date.now() - 60 * 60 * 1000
    return [
      {
        timestamp: new Date(t),
        createSuccess: new Date(t + 60 * 1000),
        recoveryPointId: 'MOCK-TEST-single',
        maxReplicationLatency: null,
      } as RecoveryPointAttemptDto,
    ]
  }
  if (scenario === 'two-points-fresh') {
    // Two attempts 5 min apart, both within the last 10 minutes.
    const now = Date.now()
    const mkAttempt = (minutesAgo: number, durationSec: number, idx: number): RecoveryPointAttemptDto => {
      const t = now - minutesAgo * 60 * 1000
      return {
        timestamp: new Date(t),
        createSuccess: new Date(t + durationSec * 1000),
        recoveryPointId: `mock-pair-${idx}`,
        maxReplicationLatency: null,
      } as RecoveryPointAttemptDto
    }
    return [mkAttempt(10, 60, 0), mkAttempt(5, 75, 1)]
  }
  if (scenario === 'all-failed') {
    // Five attempts in the last hour, none with createSuccess. The
    // successfulRecoveryPointAttempts filter strips them all → empty
    // snapshotData → "No recent data" overlay.
    const now = Date.now()
    const attempts: RecoveryPointAttemptDto[] = []
    for (let i = 0; i < 5; i++) {
      const t = now - (i + 1) * 10 * 60 * 1000
      attempts.push({
        timestamp: new Date(t),
        createSuccess: null,
        recoveryPointId: `mock-failed-${i}`,
        maxReplicationLatency: null,
      } as RecoveryPointAttemptDto)
    }
    return attempts
  }
  if (scenario === 'long-history') {
    // 60 days of attempts, ending ~10 min ago.
    return buildAttempts(60, 10 / (60 * 24))
  }
  if (scenario === 'real-time') {
    // rpo=0 mode — chart switches to replication-latency rendering.
    // Generate attempts with non-null maxReplicationLatency every 5 min
    // for the last 4 hours. The chart's `showReplicationLatencies` flag
    // gates on rpo === 0 + at least one non-null replication value.
    const now = Date.now()
    const attempts: RecoveryPointAttemptDto[] = []
    let i = 0
    for (let t = now - 4 * 60 * 60 * 1000; t <= now - 2 * 60 * 1000; t += FIVE_MIN_MS) {
      // Latency drifts between ~3s and ~30s with deterministic variation.
      const latencySec = 3 + 12 * (Math.sin(i * 0.27) + 1)
      attempts.push({
        timestamp: new Date(t),
        createSuccess: new Date(t + 30 * 1000),
        recoveryPointId: `mock-rt-${i}`,
        maxReplicationLatency: Math.round(latencySec * 1000),
      } as RecoveryPointAttemptDto)
      i++
    }
    return attempts
  }
  if (scenario === 'burst-then-quiet') {
    // Dense 12-hour burst centered ~8 days ago, then nothing. The last
    // data point sits at ~7.5 days ago, inside the 2W window but outside
    // 1W. Visually striking shape: a tall narrow cluster on an otherwise
    // empty timeline.
    return buildAttempts(8.5, 7.5)
  }
  if (scenario === 'boundary-7d') {
    // Last point lands exactly 7d ago. Tests whether `hasDataIn(7d)`'s
    // `d.x >= now - 7d` boundary condition includes the point (1W picked)
    // or excludes it (escalates to 2W).
    return buildAttempts(14, 7)
  }
  // staleNd → match the digits between "stale" and "d" so a single
  // generator handles every day in STALE_DAYS without a hand-typed switch.
  const match = /^stale(\d+)d$/.exec(scenario)
  if (match) {
    const days = parseInt(match[1], 10)
    const historyEndDaysAgo = Math.max(days + 7, 14)
    const lastAttemptDaysAgo = days + 0.5
    return buildAttempts(historyEndDaysAgo, lastAttemptDaysAgo)
  }
  return null
}
